import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Box,
  Hidden,
  CardContent,
  CardHeader,
  Card,
  Divider,
  Alert,
  AlertColor,
  Toolbar,
  TextField,
  MenuItem,
  ListItemText,
  Checkbox
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { connect } from 'react-redux';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import FleetIoService from '../services/FleetIoService';
import { formatErrorMessage, getDaysFromNow } from '../utils/common';
import { hide, show } from '../store/actions/busyIndicatorActions';
import GlobalSearch from '../components/GlobalSearch';

interface Props {
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
  fleetIoId: number,
  stateId: number,
  odometer: number
}

interface ServiceReminder {
  id: number;
  active: boolean;
  vehicle_id: number;
  vehicle_renewal_type_id: number;
  vehicle_renewal_type: string;
  next_due_at: string;
  last_sent_at: any;
  due_soon_at: string;
  due_soon_time_threshold_interval: number;
  due_soon_time_threshold_frequency: string;
  vehicle_archived_at: any;
  vehicle_renewal_reminder_status: string;
  comments_count: number;
  created_at: string;
  updated_at: string;
  vehicle: any;
}

interface ReminderType {
  id: number;
  account_id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface Warning {
  type: AlertColor;
  category: string;
  message: string;
}

const VehicleRenewalReminders: React.FC<Props> = ({
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator,
  fleetIoId,
  stateId,
  odometer
}) => {
  const [openRows, setOpenRows] = useState<{ [key: number]: boolean }>({});
  const [remindersData, setRemindersData] = useState<{ records: ServiceReminder[] }>({ records: [] });
  const [warningsData, setWarningsData] = useState<Warning[]>([]);
  const [searchText, setSearchText] = useState('');
  const [filterByCol, setFilterByCol] = useState('all_col');
  const [filteredRecords, setFilteredRecords] = useState<ServiceReminder[]>([]);
  const [selectedRenewalTypes, setSelectedRenewalTypes] = useState<number[]>([]);
  const [renewalTypes, setRenewalTypes] = useState<ReminderType[]>([]);

  const columns = [
    {
      field: 'vehicle_name',
      headerName: 'Vehicle'
    },
    {
      field: 'renewal_type',
      headerName: 'Renewal Type'
    },
    {
      field: 'next_due_at',
      headerName: 'Next Due At'
    },
    {
      field: 'due_soon_at',
      headerName: 'Due Soon At'
    },
    {
      field: 'status',
      headerName: 'Status'
    }
  ];

  const getColValue = (record: ServiceReminder, col: string) => {
    switch (col) {
      case 'vehicle_name':
        return record.vehicle?.name || '';
      case 'renewal_type':
        return record.vehicle_renewal_type || '';
      case 'next_due_at':
        return new Date(record.next_due_at).toLocaleString() || '';
      case 'due_soon_at':
        return new Date(record.due_soon_at).toLocaleString() || '';
      case 'status':
        return record.vehicle_renewal_reminder_status || '';
      default:
        return '';
    }
  };

  const applyFilters = (records: ServiceReminder[], searchedText: string, selectedTypes: number[]) => {
    let filtered = [...records];

    // Apply renewal type filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter((record) => selectedTypes.includes(record.vehicle_renewal_type_id));
    }

    // Apply search text filter
    if (searchedText) {
      filtered = filtered.filter((record: ServiceReminder) => {
        // Filter only via selected column
        if (filterByCol !== 'all_col') {
          return getColValue(record, filterByCol).toLowerCase().includes(searchedText.toLowerCase());
        }

        // Filter over all searchable fields
        return (record.vehicle?.name || '').toLowerCase().includes(searchedText.toLowerCase())
          || (record.vehicle_renewal_type || '').toLowerCase().includes(searchedText.toLowerCase())
          || (new Date(record.next_due_at).toLocaleString() || '').toLowerCase().includes(searchedText.toLowerCase())
          || (new Date(record.due_soon_at).toLocaleString() || '').toLowerCase().includes(searchedText.toLowerCase())
          || (record.vehicle_renewal_reminder_status || '').toLowerCase().includes(searchedText.toLowerCase());
      });
    }

    return filtered;
  };

  const onSearchTextChange = (event: any) => {
    const text = event.target.value;
    setSearchText(text);
    setFilteredRecords(applyFilters(remindersData?.records || [], text, selectedRenewalTypes));
  };

  const getReminders = (reminderTypes: ReminderType[]) => {
    showBusyIndicator();
    const payload = {
      actionId: 600,
      stateId,
      queryParams: {
        vehicleId: fleetIoId.toString()
      }
    };

    FleetIoService.fleetIoPage(payload)
      .then((resp: any) => {
        hideBusyIndicator();
        const response = resp;

        if (response && response?.records?.length > 0) {
          resp?.records?.forEach((reminder: ServiceReminder) => {
            reminder.vehicle_renewal_type = reminderTypes.find((x) => x.id === reminder.vehicle_renewal_type_id)?.name ?? '--';
          });
        }

        setRemindersData(resp);
        setFilteredRecords(resp?.records || []);

        const now = new Date();
        const warnings: Warning[] = [];

        if (resp && resp?.records?.length > 0) {
          resp?.records?.forEach((reminder: ServiceReminder) => {
            const dueSoonAt = reminder?.due_soon_at ? new Date(reminder.due_soon_at) : null;
            const nextDueAt = reminder.next_due_at ? new Date(reminder.next_due_at) : null;

            if (nextDueAt && nextDueAt < now) {
              // eslint-disable-next-line prefer-template
              const message = reminder?.vehicle_renewal_type + ' is overdue by ' + getDaysFromNow(reminder?.next_due_at) + ' days.';
              warnings.push({
                type: 'error',
                category: 'insurance',
                message,
              });
            } else if (dueSoonAt && dueSoonAt < now) {
              // eslint-disable-next-line prefer-template
              const message = reminder?.vehicle_renewal_type + ' due in ' + getDaysFromNow(reminder?.next_due_at) + ' days.';
              warnings.push({
                type: 'warning',
                category: 'insurance',
                message,
              });
            }
          });
        }

        setWarningsData(warnings);
      }).catch((err: any) => {
        hideBusyIndicator();
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  const getReminderTypes = () => {
    const payload = {
      actionId: 700,
      stateId
    };

    FleetIoService.fleetIoPage(payload)
      .then((resp: any) => {
        setRenewalTypes(resp?.records || []);
        getReminders(resp?.records ?? []);
      }).catch((err: any) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  const refreshData = () => {
    getReminderTypes();
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleRenewalTypeSelect = (
    event: React.ChangeEvent<HTMLInputElement> & { target: { value: number[] } }
  ) => {
    const selectedIds = event.target.value;
    setSelectedRenewalTypes(selectedIds);
    setFilteredRecords(applyFilters(remindersData?.records || [], searchText, selectedIds));
  };

  const onFilterByColumnChange = (col: string) => {
    setFilterByCol(col);
    // Re-run filters with new column filter
    setFilteredRecords(applyFilters(remindersData?.records || [], searchText, selectedRenewalTypes));
  };

  return (
    <>
      {remindersData && (
        <div>
          <Toolbar style={{ justifyContent: 'flex-end', gap: '30px' }}>
            <TextField
              sx={{ width: '200px' }}
              size="small"
              label="Renewal Types"
              name="renewalTypes"
              onChange={handleRenewalTypeSelect}
              select
              SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((x: any) => renewalTypes.find((type: ReminderType) => type.id === x)?.name).join(', ')
              }}
              value={selectedRenewalTypes}
            >
              {renewalTypes.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Checkbox checked={selectedRenewalTypes.includes(option.id)} />
                  <ListItemText primary={option.name} />
                </MenuItem>
              ))}
            </TextField>

            <GlobalSearch
              searchedText={searchText}
              onSearchTextChange={onSearchTextChange}
              showFilterByColumn
              columns={columns}
              filterByColumn={filterByCol}
              onFilterByColumnChange={onFilterByColumnChange}
              width="300px"
            />
          </Toolbar>

          {warningsData?.map((alert) => (
            <Alert sx={{ margin: 1 }} key={alert.message} severity={alert.type}>{alert.message}</Alert>
          ))}

          <Hidden mdDown>
            {/* Desktop/Table Layout */}
            <TableContainer component={Paper} elevation={3} sx={{ mt: 2, borderRadius: 2 }}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Vehicle</strong></TableCell>
                    <TableCell><strong>Renewal Type</strong></TableCell>
                    <TableCell><strong>Next Due At</strong></TableCell>
                    <TableCell><strong>Due Soon At</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecords.length > 0 ? (
                    filteredRecords.map((record) => (
                      <React.Fragment key={record.id}>
                        <TableRow>
                          <TableCell>{record.vehicle.name}</TableCell>
                          <TableCell>{record.vehicle_renewal_type}</TableCell>
                          <TableCell>{new Date(record.next_due_at).toLocaleString()}</TableCell>
                          <TableCell>{new Date(record.due_soon_at).toLocaleString()}</TableCell>
                          <TableCell>{record.vehicle_renewal_reminder_status}</TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body1" sx={{ py: 2 }}>No reminders found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Hidden>

          <Hidden mdUp>
            {/* Mobile/Responsive Layout */}
            {filteredRecords.length > 0 ? (
              filteredRecords.map((record) => (
                <Card key={record.id} sx={{ mb: 2 }}>
                  <CardHeader
                    title={record.vehicle_renewal_type}
                  />
                  <Divider />
                  <CardContent>
                    <Typography variant="body2"><strong>Vehicle:</strong> {record.vehicle.name}</Typography>
                    <Typography variant="body2"><strong>Next Due At:</strong> {new Date(record.next_due_at).toLocaleString()}</Typography>
                    <Typography variant="body2"><strong>Due Soon At:</strong> {new Date(record.due_soon_at).toLocaleString()}</Typography>
                    <Typography variant="body2"><strong>Status:</strong> {record.vehicle_renewal_reminder_status}</Typography>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography variant="body1" sx={{ py: 2 }}>No renewal reminders found.</Typography>
            )}
          </Hidden>
        </div>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts()),
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
});

export default connect(null, mapDispatchToProps)(VehicleRenewalReminders);
